import { pacientesStore } from './firebase';

/**
 * Importación ÚNICA (bootstrap) del catálogo de médicos a Firestore,
 * disparada desde App.js justo después de iniciar sesión.
 *
 * Usa el mismo SDK de CLIENTE que ya usa toda la app (`pacientesStore`) —
 * NO usa firebase-admin ni ninguna credencial de servicio, porque eso
 * jamás debe viajar al navegador (cualquiera podría extraerlo desde
 * DevTools y obtener acceso total de administrador a la base de datos).
 * Por eso esta escritura queda sujeta a las reglas de seguridad de
 * Firestore vigentes, igual que cualquier otra escritura de la app.
 *
 * Para que NO se repita en cada carga de cada usuario:
 *   1. Se consulta un documento "bandera" (_migrations/medicosBitacora).
 *      Si ya dice completado:true, la función termina de inmediato sin
 *      descargar el JSON (6 MB) ni tocar la colección de médicos.
 *   2. Antes de empezar el trabajo pesado, se "reserva" la migración con
 *      una transacción atómica — así, si dos personas abren la app casi
 *      al mismo tiempo antes de que exista la bandera, solo UNA de ellas
 *      ejecuta la importación real; la otra ve que ya está en progreso
 *      (o completada) y no hace nada.
 *   3. Si la migración quedara "atorada" en enProgreso (por ejemplo, se
 *      cerró el navegador a la mitad), se permite reintentar después de
 *      10 minutos — evita que un fallo bloquee la importación para
 *      siempre.
 */

const COLECCION_MEDICOS = 'medicosBitacora';
const RUTA_JSON = '/data/medicos_export.json';
const LOTE_MAX = 450; // Firestore permite hasta 500 operaciones por batch

const MIGRACIONES_COLECCION = '_migrations';
const MIGRACION_ID = 'medicosBitacora';
const MINUTOS_ANTES_DE_REINTENTAR_SI_QUEDO_ATORADA = 10;

const curpValido = (c) => typeof c === 'string' && c.trim().length >= 10;

/** Intenta "reservar" la migración de forma atómica. Devuelve true solo
 *  si este cliente es quien debe ejecutarla. */
const reservarMigracion = async (migRef) => {
  return pacientesStore.runTransaction(async (tx) => {
    const snap = await tx.get(migRef);
    const data = snap.exists ? snap.data() : null;

    if (data?.completado) return false;

    if (data?.enProgreso) {
      const iniciadoEl = data.iniciadoEl ? new Date(data.iniciadoEl) : null;
      const minutosTranscurridos = iniciadoEl ? (Date.now() - iniciadoEl.getTime()) / 60000 : Infinity;
      if (minutosTranscurridos < MINUTOS_ANTES_DE_REINTENTAR_SI_QUEDO_ATORADA) {
        return false; // otro cliente ya la está corriendo, no interferir
      }
      // Quedó atorada hace rato: se permite reintentar
    }

    tx.set(migRef, { enProgreso: true, iniciadoEl: new Date().toISOString() });
    return true;
  });
};

export const ejecutarImportacionMedicosSiHaceFalta = async () => {
  const migRef = pacientesStore.collection(MIGRACIONES_COLECCION).doc(MIGRACION_ID);

  try {
    // Chequeo rápido y barato antes de intentar la transacción: en el
    // 99.9% de las cargas (una vez ya migrado) esto es lo único que pasa.
    const migSnap = await migRef.get();
    if (migSnap.exists && migSnap.data()?.completado) return;

    const debeCorrerYo = await reservarMigracion(migRef);
    if (!debeCorrerYo) return;

    console.log('[importarMedicos] Iniciando importación única de médicos…');

    const resp = await fetch(RUTA_JSON);
    if (!resp.ok) throw new Error(`No se pudo obtener ${RUTA_JSON}: ${resp.status}`);
    const registros = await resp.json();

    // CURPs ya existentes en la colección (por si alguna vez se cargó algo a mano)
    const existentesSnap = await pacientesStore.collection(COLECCION_MEDICOS).get();
    const curpsExistentes = new Set();
    existentesSnap.forEach((doc) => {
      const c = doc.data()?.curp;
      if (curpValido(c)) curpsExistentes.add(c.trim().toUpperCase());
    });

    const nuevos = [];
    registros.forEach((registro) => {
      const curp = curpValido(registro.curp) ? registro.curp.trim().toUpperCase() : null;
      if (curp && curpsExistentes.has(curp)) return; // ya existe, se omite
      nuevos.push(curp ? { ...registro, curp } : registro);
    });

    console.log(`[importarMedicos] ${nuevos.length} médicos nuevos de ${registros.length} en el archivo (${registros.length - nuevos.length} ya existían por CURP).`);

    let escritos = 0;
    for (let i = 0; i < nuevos.length; i += LOTE_MAX) {
      const lote = nuevos.slice(i, i + LOTE_MAX);
      const batch = pacientesStore.batch();
      lote.forEach((data) => {
        const ref = pacientesStore.collection(COLECCION_MEDICOS).doc(); // ID interno de Firestore
        batch.set(ref, { ...data, importadoEl: new Date().toISOString() });
      });
      await batch.commit();
      escritos += lote.length;
    }

    await migRef.set({
      completado: true,
      enProgreso: false,
      totalImportados: escritos,
      totalEnArchivo: registros.length,
      fecha: new Date().toISOString(),
    });

    console.log(`[importarMedicos] Listo: ${escritos} médicos nuevos importados a "${COLECCION_MEDICOS}".`);
  } catch (err) {
    console.error('[importarMedicos] Error durante la importación automática:', err);
    // Se libera la reserva para permitir reintentar en una carga futura,
    // sin esperar los 10 minutos del timeout por atoramiento.
    try { await migRef.set({ enProgreso: false }, { merge: true }); } catch { /* noop */ }
    // No se relanza el error: un fallo aquí no debe romper el login ni la
    // carga normal de la Agenda para el usuario.
  }
};